'use client';

import { useState } from 'react';
import { db, app } from '@/lib/firebase'; 
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { motion } from 'framer-motion';

const SERVICES = [
  "Strony WWW", 
  "SEO", 
  "Tożsamość cyfrowa", 
  "Pozyskiwanie klientów High Ticket"
];

export default function ContactForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: ""
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const selectService = (service: string) => {
    setFormData({ ...formData, service: service });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.service) {
      alert("Proszę wybrać usługę.");
      return;
    }
    
    setStatus("submitting");

    try {
      // 1. Zapis do bazy danych Firestore
      await addDoc(collection(db, "leads"), {
        ...formData,
        source: "main_contact_section",
        createdAt: serverTimestamp(),
        status: "new"
      });

      // 2. Wywołanie Firebase Cloud Function (V2)
      const functions = getFunctions(app, 'europe-west1');
      const sendContactEmail = httpsCallable(functions, 'sendContactEmail');
      await sendContactEmail(formData);

      setStatus("success");
      setFormData({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (error) {
      console.error("Błąd podczas wysyłki:", error);
      setStatus("error");
    }
  };

  return (
    <section id="kontakt" className="bg-white py-20 lg:py-32 relative z-30 border-t border-gray-100 font-sans">
      <div className="mx-auto max-w-[1250px] px-6 relative z-10">
        
        {/* UKŁAD DWUKOLUMNOWY - Wyrównany do góry na desktopie */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-start">
          
          {/* LEWA KOLUMNA: INFORMACJE KONTAKTOWE */}
          <div className="lg:pt-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-xs font-bold uppercase tracking-widest mb-6">
               Kontakt
            </div>
            <h2 className="text-4xl lg:text-6xl font-bold tracking-tight text-gray-900 mb-6 leading-[1.1]">
              Porozmawiajmy o Twoim <span className="text-blue-600">projekcie.</span>
            </h2>
            <p className="text-gray-600 text-lg mb-10 leading-relaxed">
              Wypełnij krótki formularz, a my przeanalizujemy Twoje potrzeby i wrócimy z propozycją rozwiązania. To nic nie kosztuje.
            </p>

            <div className="space-y-8">
              {/* Sekcja E-mail */}
              <div className="flex items-center gap-5 text-black">
                <div className="w-12 h-12 rounded-2xl bg-gray-50 flex items-center justify-center text-blue-600 border border-gray-100 shadow-sm">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                  </svg>
                </div>
                <div className="group">
                  <p className="text-gray-400 text-xs uppercase tracking-widest font-bold">Napisz do nas</p>
                  <p className="font-bold text-xl text-black hover:text-blue-600 transition-colors cursor-pointer">kontakt@webpol-solutions.pl</p>
                </div>
              </div>

              {/* Sekcja Telefon */}
              <div className="flex items-center gap-5 text-black">
                <div className="w-12 h-12 rounded-2xl bg-gray-50 flex items-center justify-center text-blue-600 border border-gray-100 shadow-sm">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.387a12.035 12.035 0 01-7.108-7.108c-.155-.44.011-.927.387-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                  </svg>
                </div>
                <div className="group">
                  <p className="text-gray-400 text-xs uppercase tracking-widest font-bold">Zadzwoń</p>
                  <p className="font-bold text-xl text-black hover:text-blue-600 transition-colors cursor-pointer">+48 123 456 789</p>
                </div>
              </div>
            </div>
          </div>

          {/* PRAWA KOLUMNA: FORMULARZ KONTAKTOWY */}
          <div className="bg-white border border-gray-100 p-8 lg:p-10 rounded-[32px] shadow-[0_20px_50px_rgba(0,0,0,0.05)] w-full">
            {status === "success" ? (
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }} 
                animate={{ opacity: 1, scale: 1 }} 
                className="py-12 text-center"
              >
                <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Wiadomość wysłana!</h3>
                <p className="text-gray-600 mt-2">Dziękujemy za kontakt. Odezwiemy się w ciągu 24h.</p>
                <button 
                  onClick={() => setStatus("idle")} 
                  className="mt-6 text-blue-600 font-bold hover:underline"
                >
                  Wyślij kolejną wiadomość
                </button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-black uppercase tracking-wider ml-1">Jak się nazywasz?</label>
                  <input required name="name" type="text" value={formData.name} onChange={handleChange} placeholder="Imię i nazwisko" className="w-full bg-gray-50 border border-gray-100 rounded-2xl px-5 py-4 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-gray-400" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-black uppercase tracking-wider ml-1">Adres e-mail</label>
                    <input required name="email" type="email" value={formData.email} onChange={handleChange} placeholder="email@firma.pl" className="w-full bg-gray-50 border border-gray-100 rounded-2xl px-5 py-4 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-gray-400" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-black uppercase tracking-wider ml-1">Numer telefonu</label>
                    <input required name="phone" type="tel" value={formData.phone} onChange={handleChange} placeholder="+48 000 000 000" className="w-full bg-gray-50 border border-gray-100 rounded-2xl px-5 py-4 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-gray-400" />
                  </div>
                </div>

                {/* KAFELKI USŁUG Z ANIMACJĄ POJAWIANIA SIĘ */}
                <div className="space-y-3">
                  <label className="text-xs font-bold text-black uppercase tracking-wider ml-1">Wybierz usługę</label>
                  <div className="flex flex-wrap gap-2">
                    {SERVICES.map((s, i) => (
                      <motion.button
                        key={s}
                        type="button"
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.1 }}
                        viewport={{ once: true }}
                        onClick={() => selectService(s)}
                        className={`px-4 py-3 rounded-xl text-sm font-medium border transition-all duration-200 ${
                          formData.service === s 
                          ? 'bg-blue-600 border-blue-600 text-white shadow-md shadow-blue-200 scale-[1.02]' 
                          : 'bg-gray-50 border-gray-100 text-gray-600 hover:border-blue-200 hover:bg-white'
                        }`}
                      >
                        {s}
                      </motion.button>
                    ))}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-black uppercase tracking-wider ml-1">Opisz swój projekt</label>
                  <textarea required name="message" value={formData.message} onChange={handleChange} rows={4} placeholder="Opisz krótko swój cel..." className="w-full bg-gray-50 border border-gray-100 rounded-2xl px-5 py-4 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-none placeholder:text-gray-400"></textarea>
                </div>

                <button 
                  disabled={status === "submitting"} 
                  type="submit" 
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-5 rounded-2xl transition-all shadow-lg active:scale-[0.98] text-lg disabled:bg-gray-400 shadow-blue-200"
                >
                  {status === "submitting" ? "Wysyłanie..." : "Wyślij wiadomość"}
                </button>

                {status === "error" && (
                  <p className="text-red-500 text-center text-xs font-bold mt-2">Wystąpił błąd podczas wysyłki. Spróbuj ponownie później.</p>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}